import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MediaCard from '@/components/base/MediaCard';
import RatingBadge from '@/components/base/RatingBadge';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { useBrowseHistory } from '@/hooks/useBrowseHistory';
import { actors, mediaList, type Actor } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { pushHistory } = useBrowseHistory();
  const [activeActor, setActiveActor] = useState<Actor | null>(null);

  const item = mediaList.find((media) => media.id === id);

  useDocumentMeta({
    title: item
      ? `${item.title} - ${item.typeLabel} - 粉色水蜜桃`
      : '影视资源不存在 - 粉色水蜜桃',
    description: item
      ? `${item.title}（${item.typeLabel}·${item.region}·${item.year}）剧情简介：${item.summary} 主演${item.cast
          .map((c) => c.name)
          .join('、')}。`
      : '该影视资源不存在或已下架，请返回粉色水蜜桃首页浏览其他作品。',
    keywords: item
      ? `${item.title},${item.typeLabel},剧情介绍,演员表,${item.genres.join(',')},粉色水蜜桃`
      : '粉色水蜜桃,影视资源',
  });

  useEffect(() => {
    if (item) pushHistory(item.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const related = useMemo(() => {
    if (!item) return [];
    return mediaList
      .filter((media) => media.id !== item.id)
      .map((media) => {
        let score = 0;
        if (media.type === item.type) score += 1;
        score += media.genres.filter((g) => item.genres.includes(g)).length * 2;
        const sharedCast = media.cast.filter((c) =>
          item.cast.some((ic) => ic.name === c.name),
        ).length;
        score += sharedCast * 3;
        return { media, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((entry) => entry.media);
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          此影视资源不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          你访问的作品可能已被移除，不妨回到分类页看看其他精彩内容。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/category"
            className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
          >
            前往影视分类
          </Link>
          <Link
            to="/"
            className="whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const castWithInfo = item.cast.map((member) => ({
    ...member,
    info: actors.find((actor) => actor.name === member.name),
  }));

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200">
        <div className="absolute inset-0">
          <img
            src={item.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover object-top opacity-30 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/90 to-background-50" />
        </div>

        <div className="relative mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/category" className="transition-colors hover:text-primary-600">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link
              to={`/category?type=${item.type}`}
              className="transition-colors hover:text-primary-600"
            >
              {item.typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">{item.title}</span>
          </nav>

          <div className="mt-5 grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
            <div className="w-[200px] overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:w-[240px] lg:w-full">
              <div className="aspect-[3/4] w-full">
                <img
                  src={item.cover}
                  alt={`${item.title}海报`}
                  title={`${item.title} 粉色水蜜桃影视`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  {item.typeLabel}
                </span>
                {item.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {item.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line text-primary-500" aria-hidden="true"></i>
                  {item.region}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-primary-500" aria-hidden="true"></i>
                  {item.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-clapperboard-line text-primary-500" aria-hidden="true"></i>
                  {item.episodes}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
                  更新于 {item.updatedAt}
                </span>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <RatingBadge rating={item.rating} level={item.scoreLevel} />
                <span className="text-sm text-foreground-600">
                  综合评分（演示数据） · 热度 {item.heat}
                </span>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.summary}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(item.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                    favorited
                      ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950'
                  }`}
                >
                  <i
                    className={favorited ? 'ri-heart-3-fill text-lg' : 'ri-heart-3-line text-lg'}
                    aria-hidden="true"
                  ></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回上一页
                </button>
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                <i className="ri-information-line" aria-hidden="true"></i> 收藏与浏览记录仅保存在本机浏览器缓存中，未登录状态不会同步至云端。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {item.synopsis}
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              演员信息
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              点击演员卡片可查看详细资料（演示数据）
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {castWithInfo.map((member) => (
                <button
                  key={member.name}
                  type="button"
                  onClick={() => member.info && setActiveActor(member.info)}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5 text-left transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={member.info?.avatar ?? item.cover}
                      alt={`${member.name}头像`}
                      title={`${member.name} 演员资料`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground-950">
                      {member.name}
                    </span>
                    <span className="mt-0.5 block line-clamp-1 text-xs text-foreground-600">
                      {member.role}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" aria-hidden="true"></i>
                </button>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <div className="flex items-end justify-between gap-3">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关影视推荐
              </h2>
              <Link
                to={`/category?type=${item.type}`}
                className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                更多{item.typeLabel}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            {related.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4 md:gap-5">
                {related.map((media) => (
                  <MediaCard key={media.id} item={media} />
                ))}
              </div>
            ) : (
              <p className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 text-sm text-foreground-500">
                暂无相关推荐，试试前往分类页浏览更多作品。
              </p>
            )}
          </section>
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-semibold text-foreground-950">作品信息</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">类型</dt>
                <dd className="text-foreground-800">{item.typeLabel}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">地区</dt>
                <dd className="text-foreground-800">{item.region}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">年份</dt>
                <dd className="text-foreground-800">{item.year}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">篇幅</dt>
                <dd className="text-foreground-800">{item.episodes}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">导演</dt>
                <dd className="text-foreground-800">{item.director}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">题材</dt>
                <dd className="text-right text-foreground-800">{item.genres.join(' / ')}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-semibold text-foreground-950">分类导航</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category?type=${item.type}`}
                  className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {g}
                </Link>
              ))}
            </div>
            <Link
              to="/category"
              className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              浏览全部影视分类
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </aside>
      </div>

      {activeActor ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setActiveActor(null)}
            aria-hidden="true"
          ></div>
          <div className="relative w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6">
            <button
              type="button"
              onClick={() => setActiveActor(null)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-background-200 text-foreground-600 cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line text-lg" aria-hidden="true"></i>
            </button>
            <div className="flex items-center gap-4">
              <span className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-background-200">
                <img
                  src={activeActor.avatar}
                  alt={`${activeActor.name}头像`}
                  title={`${activeActor.name} 演员资料`}
                  className="h-full w-full object-cover object-top"
                />
              </span>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  {activeActor.name}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">演员资料 · 演示数据</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">{activeActor.bio}</p>
            <p className="mt-4 text-xs text-foreground-500">
              参演作品：《{item.title}》等（示例）
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}